import React from 'react';

import { useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { EntityType } from '@atlaskit/smart-common';
import {
	getTeamPath,
	getUserPath,
	highlightMatchedText,
} from '@atlassian/dynamic-cards-search-common';

import { SearchLink } from '../../../../../common/ui/search-link';
import { type EventWithTarget } from '../../../../../common/ui/search-link/types';
import {
	useCloudId,
	useDropdown,
	useInputQuery,
	useOrgId,
	usePrimaryProduct,
} from '../../../../../controllers/store';
import {
	usePeopleAndTeamsResults,
	useResultCount,
} from '../../../../../controllers/store/dropdown';

import { messages } from './messages';

export const PeopleAndTeamsResults = (): React.JSX.Element => {
	const intl = useIntl();

	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [inputQuery] = useInputQuery();
	const [primaryProduct] = usePrimaryProduct();
	const [, { setShowDropdown }] = useDropdown();
	const [resultCount] = useResultCount();
	const [peopleAndTeamsResults] = usePeopleAndTeamsResults();

	const onClick = () => {
		setShowDropdown(false);
	};

	return (
		<>
			{peopleAndTeamsResults.results.slice(0, resultCount.peopleAndTeams).map((result, index) => {
				if (result.entityType === EntityType.USER && result.name) {
					const navigationUrl =
						primaryProduct &&
						cloudId &&
						orgId &&
						getUserPath(result.id, primaryProduct, orgId, cloudId);

					return (
						<PeopleAndTeamsLink
							title={result.name}
							description={result.title}
							avatarUrl={result.avatarUrl}
							onClick={onClick}
							inputQuery={inputQuery}
							key={`${index}-${result.name}`}
							navigationUrl={navigationUrl || ''}
						/>
					);
				} else if (result.entityType === EntityType.TEAM && result.displayName) {
					const navigationUrl =
						primaryProduct &&
						cloudId &&
						orgId &&
						getTeamPath(result.id, primaryProduct, orgId, cloudId);

					return (
						<PeopleAndTeamsLink
							title={result.displayName}
							description={intl.formatMessage(messages.teamLabel)}
							avatarUrl={result?.smallAvatarImageUrl}
							onClick={onClick}
							inputQuery={inputQuery}
							key={`${index}-${result.name}`}
							navigationUrl={navigationUrl || ''}
						/>
					);
				}
			})}
		</>
	);
};

const iconStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.full',
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

type PeopleAndTeamsLinkProps = {
	avatarUrl?: string;
	description?: string;
	inputQuery: string;
	key: string;
	navigationUrl: string;
	onClick: (event: EventWithTarget) => void;
	title: string;
};

const PeopleAndTeamsLink = ({
	title,
	description = '',
	avatarUrl = '',
	inputQuery,
	onClick,
	navigationUrl,
}: PeopleAndTeamsLinkProps) => (
	<SearchLink
		title={title}
		description={description}
		icon={
			<Box xcss={iconStyles}>
				<Avatar src={avatarUrl} size="small" />
			</Box>
		}
		onClick={onClick}
		highlightedTitle={highlightMatchedText(title, inputQuery.split(' ')).highlightedTitle}
		navigationUrl={navigationUrl}
	/>
);
